import React, { useMemo, useState } from 'react';
import { Download, Loader2, Image as ImageIcon, Palette, FileVideo, FileText, Archive, CheckSquare, X } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { contentCalendarAPI } from '../lib/api';
import { CONTENT_STATES } from '../lib/contentStates';

export const triggerFileDownload = (url) => {
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const downloadPostsZip = async (ids) => {
  if (!ids.length) return;
  const tId = toast.loading(`Preparando ZIP de ${ids.length} ${ids.length === 1 ? 'publicación' : 'publicaciones'}...`);
  try {
    await contentCalendarAPI.downloadZip(ids);
    toast.success('Descarga iniciada', { id: tId });
  } catch (e) {
    toast.error(e?.message || 'No se pudo generar el ZIP', { id: tId });
  }
};

const kindIcon = (k) => (k === 'video' ? FileVideo : k === 'image' ? ImageIcon : FileText);

export const PostDownloadMenu = ({ post, className = '' }) => {
  const [files, setFiles] = useState(null);
  const load = async (open) => {
    if (!open) return;
    setFiles(null);
    try {
      const full = await contentCalendarAPI.get(post.id);
      setFiles({ portada: full.portada_file_id, color: full.color_portada_file_id, attachments: full.attachments || [] });
    } catch { setFiles({ portada: null, color: null, attachments: [] }); }
  };
  const dl = (fileId) => triggerFileDownload(contentCalendarAPI.getAttachmentDownloadUrl(post.id, fileId));
  const hasAny = files && (files.portada || files.color || files.attachments.length > 0);
  return (
    <DropdownMenu onOpenChange={load}>
      <DropdownMenuTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className={`p-0.5 rounded hover:bg-muted ${className}`}
          title="Descargar material"
          data-testid={`post-download-btn-${post.id}`}
        >
          <Download className="w-3 h-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" onClick={(e) => e.stopPropagation()} data-testid={`post-download-menu-${post.id}`}>
        <DropdownMenuLabel className="text-xs truncate">{post.titulo || 'Sin título'}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {!files ? (
          <div className="py-3 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
        ) : !hasAny ? (
          <div className="py-3 px-2 text-xs text-muted-foreground text-center" data-testid={`post-download-empty-${post.id}`}>Esta publicación no tiene archivos</div>
        ) : (
          <>
            {files.portada && (
              <DropdownMenuItem onSelect={() => dl(files.portada)} data-testid={`post-download-portada-${post.id}`}>
                <ImageIcon className="w-4 h-4 mr-2" /> Portada
              </DropdownMenuItem>
            )}
            {files.color && (
              <DropdownMenuItem onSelect={() => dl(files.color)} data-testid={`post-download-color-${post.id}`}>
                <Palette className="w-4 h-4 mr-2" /> Creatividad / publicación
              </DropdownMenuItem>
            )}
            {files.attachments.length > 0 && <DropdownMenuLabel className="text-[11px] text-muted-foreground pt-2">Material adjunto</DropdownMenuLabel>}
            {files.attachments.map((a) => {
              const Icon = kindIcon(a.kind);
              return (
                <DropdownMenuItem key={a.file_id} onSelect={() => dl(a.file_id)} data-testid={`post-download-file-${a.file_id}`}>
                  <Icon className="w-4 h-4 mr-2 shrink-0" /> <span className="truncate">{a.name || 'Archivo'}</span>
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => downloadPostsZip([post.id])} className="font-medium" data-testid={`post-download-zip-${post.id}`}>
              <Archive className="w-4 h-4 mr-2" /> Todo en un ZIP
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const useDownloadSelection = () => {
  const [active, setActive] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const setMany = (ids) => setSelected(new Set(ids));
  const stop = () => { setActive(false); setSelected(new Set()); };
  return { active, start: () => setActive(true), stop, selected, toggle, setMany };
};

export const DownloadSelectionBar = ({ selection, visiblePosts }) => {
  const [busy, setBusy] = useState(false);
  const stateCounts = useMemo(() => {
    const c = {};
    for (const p of visiblePosts) c[p.estado] = (c[p.estado] || 0) + 1;
    return c;
  }, [visiblePosts]);
  if (!selection.active) {
    return (
      <Button variant="outline" size="sm" onClick={selection.start} data-testid="download-select-mode-btn">
        <CheckSquare className="w-4 h-4 mr-1.5" /> Seleccionar para descargar
      </Button>
    );
  }
  const count = selection.selected.size;
  const run = async () => { setBusy(true); await downloadPostsZip([...selection.selected]); setBusy(false); };
  return (
    <div className="flex items-center gap-2 flex-wrap rounded-md border border-primary/40 bg-primary/5 px-2 py-1.5" data-testid="download-selection-bar">
      <span className="text-xs font-semibold" data-testid="download-selected-count">{count} seleccionadas</span>
      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => selection.setMany(visiblePosts.map((p) => p.id))} data-testid="download-select-all-visible">
        Todas las visibles ({visiblePosts.length})
      </Button>
      <Select value="" onValueChange={(v) => selection.setMany(visiblePosts.filter((p) => p.estado === v).map((p) => p.id))}>
        <SelectTrigger className="h-7 w-[200px] text-xs" data-testid="download-select-by-state">
          <SelectValue placeholder="Seleccionar por estado" />
        </SelectTrigger>
        <SelectContent>
          {CONTENT_STATES.filter((s) => stateCounts[s.id]).map((s) => (
            <SelectItem key={s.id} value={s.id} data-testid={`download-select-state-${s.id}`}>{s.label} ({stateCounts[s.id]})</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {count > 0 && (
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => selection.setMany([])} data-testid="download-clear-selection">Quitar selección</Button>
      )}
      <Button size="sm" className="h-7 text-xs" disabled={!count || busy} onClick={run} data-testid="download-selected-zip-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Descargar seleccionadas (ZIP)
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={selection.stop} title="Salir de la selección" data-testid="download-select-exit">
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
};
